import { useCallback, useMemo, useState } from 'react';

import dlv from 'dlv';
import { useIntl } from 'react-intl';

import {
  useRewardRedemptionDetailsQuery,
  useSetRewardsRedemptionEnabledMutation,
} from 'generated/graphql-gateway';
import { GetMeDocument, useSetLoyaltyRewardsTierMutation } from 'generated/rbi-graphql';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import { useAuthContext } from 'state/auth';
import { NUM_RECENT_PURCHASED_ITEMS } from 'state/auth/hooks/use-current-user';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLogger } from 'state/logger';
import { ILoyaltyCategory } from 'state/loyalty/types';

import { IUseSelectedReward } from './types';
import { useBankMyRewardsTierId } from './use-bank-my-rewards-tier-id';
import { useLoyaltyCategories } from './use-loyalty-categories';

type LoyaltyCategoryOption = ILoyaltyCategory['options'][number];

const formatLoyaltyCategories = (loyaltyCategories: ILoyaltyCategory[]) =>
  loyaltyCategories.reduce((acc: { [key: string]: LoyaltyCategoryOption }, { options }) => {
    options.forEach(val => (acc[val._id] = val));
    return acc;
  }, {});

export const useSelectedReward = (): IUseSelectedReward => {
  const { user } = useAuthContext();

  const { data: rewardsData } = useRewardRedemptionDetailsQuery();
  const shouldUniqueByModifiers = useFlag(LaunchDarklyFlag.ENABLE_RECENT_ITEMS_WITH_MODIFIERS);
  const logger = useLogger();
  const { activeCard } = useLoyaltyActiveCard();
  const loyaltyCategories = useLoyaltyCategories();
  const [setSelectedRewardsTier] = useSetLoyaltyRewardsTierMutation();
  const [setRewardsRedemptionEnabled] = useSetRewardsRedemptionEnabledMutation();
  const { bankMyRewardTier } = useBankMyRewardsTierId();
  const { formatMessage } = useIntl();

  const { cardId = '' } = activeCard || {};

  const selectedRewardTierId = useMemo(() => dlv(user || {}, 'details.loyaltyTier') ?? null, [
    user,
  ]);

  const isRedemptionEnabled = useMemo(
    () => dlv(rewardsData || {}, 'me.rewards.redemption.isEnabled') ?? null,
    [rewardsData]
  );

  const [updatingRewardTier, setUpdatingRewardTier] = useState(false);
  const [errorUpdatingRewardTier, setErrorUpdatingRewardTier] = useState(false);

  const onChangeRewardTier = useCallback(
    async (newLoyaltyTierId: string | null) => {
      if (!newLoyaltyTierId || !cardId) {
        return;
      }

      setUpdatingRewardTier(true);

      try {
        await setSelectedRewardsTier({
          variables: {
            cardId,
            rewardsTier: newLoyaltyTierId,
          },
          refetchQueries: [
            {
              query: GetMeDocument,
              variables: {
                customInput: { shouldUniqueByModifiers },
                numUniquePurchasedItems: NUM_RECENT_PURCHASED_ITEMS,
              },
            },
          ],
          awaitRefetchQueries: true,
        });
      } catch (error) {
        logger.error({ error });
        setErrorUpdatingRewardTier(true);
      } finally {
        setUpdatingRewardTier(false);
      }
    },
    [cardId, logger, setSelectedRewardsTier, shouldUniqueByModifiers]
  );

  const onToggleRewardsRedemption = useCallback(
    async (isEnabled: boolean) => {
      setUpdatingRewardTier(true);

      try {
        await setRewardsRedemptionEnabled({
          variables: {
            input: { isEnabled },
          },
          refetchQueries: [
            {
              query: GetMeDocument,
              variables: {
                customInput: { shouldUniqueByModifiers },
                numUniquePurchasedItems: NUM_RECENT_PURCHASED_ITEMS,
              },
            },
          ],
          awaitRefetchQueries: true,
        });
      } catch (error) {
        logger.error({ error });
        setErrorUpdatingRewardTier(true);
      } finally {
        setUpdatingRewardTier(false);
      }
    },
    [logger, setRewardsRedemptionEnabled, shouldUniqueByModifiers]
  );

  const dismissUpdateError = useCallback(() => {
    setErrorUpdatingRewardTier(false);
  }, []);

  const selectedRewardTier = useMemo(() => {
    if (bankMyRewardTier && isRedemptionEnabled === false) {
      return {
        // Returning this with a formatted name as opposed to the bankMyRewardTier object
        // because the query for bankMyRewardTier does't convert the name attribute to locale
        // TODO: RN - improve query to return correctly formatted object
        name: { locale: formatMessage({ id: 'bankMyRewards' }) },
        description: bankMyRewardTier.description,
        points: bankMyRewardTier.points,
        _id: bankMyRewardTier._id,
      };
    }
    if (loyaltyCategories) {
      const loyaltyCategoriesById = formatLoyaltyCategories(loyaltyCategories);
      return loyaltyCategoriesById[selectedRewardTierId ?? ''];
    }
    return undefined;
  }, [
    bankMyRewardTier,
    selectedRewardTierId,
    loyaltyCategories,
    formatMessage,
    isRedemptionEnabled,
  ]);

  const selectedRewardCategoryId = useMemo(() => {
    if (loyaltyCategories && loyaltyCategories.length) {
      const selected = loyaltyCategories.find(({ options }) =>
        options.find(({ _id }) => _id === selectedRewardTierId)
      );
      return selected?._id;
    }
    return undefined;
  }, [loyaltyCategories, selectedRewardTierId]);

  return {
    dismissUpdateError,
    errorUpdatingRewardTier,
    isRedemptionEnabled,
    selectedRewardCategoryId,
    selectedRewardTier,
    selectedRewardTierId,
    onChangeRewardTier,
    onToggleRewardsRedemption,
    updatingRewardTier,
  };
};
